import type { QuoteRulesetVersionRulePublic } from "@common/client"
import { z } from "zod"
import type { GroupLabel } from "@/lib/grouping"
import {
  personName,
  wasUpdated,
} from "@/routes/_layout/settings/_panel/-lib/history"
import { type RuleRecord, ruleRecords, versionRecord } from "./history"
import { usePlaybook } from "./playbook"

export const RULE_GROUPINGS = ["created_by", "updated_by"] as const

export const ruleGroupingSchema = z.enum(RULE_GROUPINGS)

export type RuleGrouping = z.infer<typeof ruleGroupingSchema>

export const RULE_GROUPING_LABELS: Record<RuleGrouping, string> = {
  created_by: "Created by",
  updated_by: "Updated by",
}

export type RuleFilters = {
  search?: string
  creator?: string
  updater?: string
}

export type RuleRow = {
  rule: QuoteRulesetVersionRulePublic
  record: RuleRecord
}

export function useRuleRows(playbookId: string): RuleRow[] {
  const { playbook, versions } = usePlaybook(playbookId)
  const version = playbook.latest_version
  if (!version) {
    return []
  }
  const records = ruleRecords(versions)
  return version.active_rules.map((rule) => ({
    rule,
    record: records.get(rule.lineage_id) ?? versionRecord(version),
  }))
}

export function updater(row: RuleRow): RuleRecord["last_modified_by"] {
  return wasUpdated(row.record) ? row.record.last_modified_by : null
}

export function matchesRuleFilters(
  row: RuleRow,
  filters: RuleFilters,
): boolean {
  const text = (filters.search ?? "").toLowerCase()
  return (
    (!text || row.rule.content.toLowerCase().includes(text)) &&
    (!filters.creator || row.record.created_by?.id === filters.creator) &&
    (!filters.updater || updater(row)?.id === filters.updater)
  )
}

export function ruleGroup(row: RuleRow, grouping: RuleGrouping): GroupLabel {
  switch (grouping) {
    case "created_by": {
      const user = row.record.created_by
      return user
        ? { key: user.id, label: personName(user) }
        : { key: "", label: "Unknown" }
    }
    case "updated_by": {
      const user = updater(row)
      return user
        ? { key: user.id, label: personName(user) }
        : { key: "", label: "Not updated" }
    }
  }
}
